/**
 * "Related parties to scan" — `GET /experience/related_parties/{dealId}?max_hops=2`,
 * as an AG Grid grouped by hop:
 *
 *   On this deal · hop 1      co-borrowers, entities on THIS deal
 *   Via shared deals · hop 2  entity members, and parties on OTHER deals the
 *                             same canonical parties appear on (cross-LOS)
 *
 * Row: kind pill · name · quiet meta (verified count / entity type / the
 * path — "member of X", "another deal") · Open ↗. Open on a borrower who is
 * on this loan opens the APPLICANT SIDEBAR in place (the same detail sidebar
 * the Applicants tab uses); an entity on this loan goes to the Entity tab; a
 * party on another deal opens that loan.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { RowGroupingModule } from "ag-grid-enterprise";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { FormNote, FormPill } from "../../../components/forms/FormPrimitives";
import { experienceApi } from "../../../api/experienceApi";
import { DETAIL_SIDEBAR_CONTENT_TYPES, detailSidebarOpen } from "../../../redux/reducers/detailSidebarReducer";
import { FORM, formSx, px } from "../../../theme/formStyles";
import { CARD_GRID_COL_DEF, CARD_GRID_SX, CARD_TABLE_BOX_SX, GRID_ROW_H } from "../shared/cardGrid";

ModuleRegistry.registerModules([RowGroupingModule]);

const HOP_LABEL = { 1: "On this deal · hop 1", 2: "Via shared deals · hop 2" };

/** The quiet text after the name — the path the backend found, made readable. */
const metaOf = (p, dealId, borrowerEntityId) => {
  const parts = [];
  const via = String(p?.via || "");
  const onThisDeal = String(p?.dealId) === String(dealId);
  if (via.startsWith("member_of:")) parts.push(`member of ${via.slice("member_of:".length)}`);
  if (p?.kind === "entity") {
    if (String(p?._id) === String(borrowerEntityId)) parts.push("borrower entity");
    else if (p?.entityType) parts.push(p.entityType);
  } else if (onThisDeal && p?.hop === 1 && p?.verifiedExperienceTotal != null) {
    parts.push(`${p.verifiedExperienceTotal} verified`);
  }
  if (!onThisDeal) parts.push("another deal");
  return parts.join(" · ");
};

const GroupRow = (params) => (
  <Flx ac sx={{ height: "100%", position: "absolute", inset: 0, px: px(FORM.s.s4), bgcolor: FORM.color.surface2 }}>
    <Txt sx={{ ...formSx.groupLabel, mb: 0 }}>{HOP_LABEL[params.node?.key] || params.node?.key}</Txt>
  </Flx>
);

const RelatedPartiesPanel = ({ dealId, currentPartyId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loan = useSelector((s) => s.loanDrilldown);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    experienceApi
      .relatedParties(dealId, 2)
      .then((d) => setData(d || {}))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, [dealId]);

  const rows = useMemo(
    () =>
      (data?.parties || [])
        .filter((p) => String(p?._id) !== String(currentPartyId))
        .map((p, i) => ({ ...p, rowId: `${p.kind}-${p._id}-${i}`, hopKey: p.hop === 1 ? 1 : 2 })),
    [data, currentPartyId],
  );

  const borrowerEntityId = loan?.borrowerEntity?._id;

  const open = useCallback((p) => {
    const onThisDeal = String(p?.dealId) === String(dealId);
    if (!onThisDeal) {
      navigate(`/loan/${p.dealId}`);
      return;
    }
    if (p.kind === "borrower") {
      const borrower = (loan?.borrowers || []).find((b) => String(b?._id) === String(p._id));
      if (borrower) {
        dispatch(
          detailSidebarOpen(DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER, borrower, borrower._id, {
            title: `${borrower.firstName || ""} ${borrower.lastName || ""}`.trim() || "Borrower",
            mode: "view",
            source: "RelatedPartiesPanel",
            sourceField: "borrowers",
          }),
        );
        return;
      }
      navigate(`/loan/${dealId}/applicants`);
      return;
    }
    navigate(`/loan/${dealId}/entity-redesign`);
  }, [dealId, loan?.borrowers, navigate, dispatch]);

  const columnDefs = useMemo(
    () => [
      { field: "hopKey", rowGroup: true, hide: true },
      {
        headerName: "Kind",
        colId: "kind",
        width: 96,
        minWidth: 96,
        maxWidth: 110,
        sortable: false,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            <FormPill>{p.data?.kind === "entity" ? "Entity" : "Borrower"}</FormPill>
          </Flx>
        ),
      },
      {
        headerName: "Party",
        colId: "party",
        flex: 1,
        minWidth: 240,
        valueGetter: (p) => p.data?.name || "Unnamed",
        cellRenderer: (p) => {
          const meta = metaOf(p.data, dealId, borrowerEntityId);
          return (
            <Flx ac g={0.75} sx={{ height: "100%", minWidth: 0 }}>
              <Txt sx={{ ...formSx.label, mb: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.value}</Txt>
              {meta ? <Txt sx={{ ...formSx.meta, whiteSpace: "nowrap" }}>· {meta}</Txt> : null}
            </Flx>
          );
        },
      },
      {
        headerName: "",
        colId: "open",
        width: 96,
        minWidth: 96,
        maxWidth: 96,
        sortable: false,
        pinned: "right",
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%", justifyContent: "flex-end" }}>
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                open(p.data);
              }}
              sx={{ ...formSx.link, display: "inline-flex", alignItems: "center", gap: px(4), background: "none", border: 0, p: 0, cursor: "pointer", fontFamily: "inherit" }}
            >
              Open <OpenInNewOutlined sx={{ fontSize: 13 }} />
            </Box>
          </Flx>
        ),
      },
    ],
    [dealId, borrowerEntityId, open],
  );

  if (loading)
    return (
      <Flx ac g={1} sx={{ py: px(FORM.s.s2) }}>
        <CircularProgress size={14} />
        <Txt sx={formSx.helper}>Scanning related parties…</Txt>
      </Flx>
    );
  if (!rows.length) return <FormNote>No related parties found within two hops.</FormNote>;

  return (
    <Box sx={CARD_TABLE_BOX_SX}>
      <AgGridTableWrapper compactTable noBorder suppressMinHeightWhenPopulated={rows} sx={{ ...CARD_GRID_SX, "& .ag-header": { display: "none" }, "& .ag-row-group": { borderBottomColor: FORM.color.divider } }}>
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={CARD_GRID_COL_DEF}
          getRowId={(p) => p.data?.rowId}
          domLayout="autoHeight"
          headerHeight={0}
          rowHeight={GRID_ROW_H}
          groupDisplayType="groupRows"
          groupRowRenderer={GroupRow}
          groupDefaultExpanded={-1}
          suppressAggFuncInHeader
          onRowClicked={(e) => (e.data ? open(e.data) : null)}
          getRowStyle={(p) => (p.node?.group ? {} : { cursor: "pointer" })}
        />
      </AgGridTableWrapper>
    </Box>
  );
};

RelatedPartiesPanel.propTypes = { dealId: PropTypes.string, currentPartyId: PropTypes.string };

export default RelatedPartiesPanel;
